import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Truck, MapPin, Clock, Headphones, Award, CheckCircle, ArrowRight, Globe, Leaf, Lock, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useSiteContent } from '../context/SiteContentContext';
import { usePageTitle } from '../hooks/usePageTitle';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WhyChooseUsPage() {
  const { language, localizedPath } = useSiteContent();
  const [dbContent, setDbContent] = useState(null);
  
  // Set page title
  usePageTitle('whyChooseUs');

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/why-choose-us-content`);
        if (response.data && Object.keys(response.data).length > 0) {
          setDbContent(response.data);
        }
      } catch (error) {
        console.error('Error fetching why choose us content:', error);
      }
    };
    fetchContent();
  }, []);

  // Default translations (used as fallback)
  const defaultContent = language === 'bg' ? {
    heroSubtitle: 'ЗАЩО ДА ИЗБЕРЕТЕ НАС',
    heroTitle: 'Вашият доверен',
    heroTitleHighlight: 'транспортен партньор',
    heroDescription: 'Съчетаваме десетилетия опит с най-новите технологии, за да предоставим изключителни транспортни услуги.',
    stats: [
      { number: '20+', label: 'Години опит' },
      { number: '50,000+', label: 'Доставени пратки' },
      { number: '99.5%', label: 'Навременност' },
      { number: '28+', label: 'Обслужени държави' }
    ],
    benefitsSubtitle: 'НАШИТЕ ПРЕДИМСТВА',
    benefitsTitle: 'Какво ни прави',
    benefitsTitleHighlight: 'различни',
    benefits: [
      { icon: Shield, title: 'Пълна застраховка', description: 'Всички пратки са покрити с цялостна застраховка на товара.', highlight: '100% покритие' },
      { icon: Truck, title: 'Модерен флот', description: 'EURO 6 превозни средства с достъп до ECO зони.', highlight: 'EURO 6 стандарт' },
      { icon: MapPin, title: 'GPS проследяване', description: 'Проследяване в реално време на всяка пратка.', highlight: 'На живо' },
      { icon: Clock, title: 'Експресна доставка', description: 'Времево критични пратки доставени в рамките на 24-40 часа.', highlight: '24-40 часа' },
      { icon: Headphones, title: '24/7 поддръжка', description: 'Нашият екип е на разположение денонощно.', highlight: 'Винаги на линия' },
      { icon: Award, title: 'Лицензиран превозвач', description: 'Напълно лицензиран EU превозвач и член на IRU.', highlight: 'IRU член' }
    ],
    guaranteesSubtitle: 'ГАРАНЦИЯ ЗА КАЧЕСТВО',
    guaranteesTitle: 'Нашите',
    guaranteesTitleHighlight: 'гаранции',
    guaranteesText: 'Стоим зад всяка пратка с нашия ангажимент за качество, надеждност и удовлетвореност на клиентите.',
    guarantees: [
      'Навременна доставка или връщане на парите',
      'Професионално боравене с всички видове товари',
      'Прозрачно ценообразуване без скрити такси',
      'Редовни актуализации по време на транспорта',
      'Гъвкави опции за планиране',
      'Помощ при митническо оформяне'
    ],
    ctaTitle: 'Готови да изпитате разликата?',
    ctaText: 'Присъединете се към хилядите доволни клиенти, които се доверяват на Tetralog за своите транспортни нужди.',
    getQuote: 'Безплатна оферта',
    contactUs: 'Свържете се с нас',
    gridItems: [
      { icon: Globe, title: 'EU покритие', sub: 'Широко' },
      { icon: Leaf, title: 'ECO', sub: 'Екологичен флот' },
      { icon: Lock, title: 'Сигурен', sub: 'Транспорт' },
      { icon: Users, title: 'Експертен', sub: 'Екип' }
    ]
  } : {
    heroSubtitle: 'WHY CHOOSE US',
    heroTitle: 'Your Trusted',
    heroTitleHighlight: 'Transport Partner',
    heroDescription: 'We combine decades of experience with cutting-edge technology to deliver exceptional transport services.',
    stats: [
      { number: '20+', label: 'Years Experience' },
      { number: '50,000+', label: 'Shipments Delivered' },
      { number: '99.5%', label: 'On-Time Rate' },
      { number: '28+', label: 'Countries Served' }
    ],
    benefitsSubtitle: 'OUR ADVANTAGES',
    benefitsTitle: 'What Sets Us',
    benefitsTitleHighlight: 'Apart',
    benefits: [
      { icon: Shield, title: 'Fully Insured', description: 'All shipments are covered by comprehensive cargo insurance.', highlight: '100% Coverage' },
      { icon: Truck, title: 'Modern Fleet', description: 'EURO 6 compliant vehicles with ECO zone access.', highlight: 'EURO 6 Standard' },
      { icon: MapPin, title: 'GPS Tracking', description: 'Real-time tracking on every shipment.', highlight: 'Live Updates' },
      { icon: Clock, title: 'Express Delivery', description: 'Time-critical shipments delivered within 24-40 hours.', highlight: '24-40 Hours' },
      { icon: Headphones, title: '24/7 Support', description: 'Our dedicated team is available around the clock.', highlight: 'Always Available' },
      { icon: Award, title: 'Licensed Carrier', description: 'Fully licensed EU carrier and IRU member.', highlight: 'IRU Member' }
    ],
    guaranteesSubtitle: 'QUALITY ASSURANCE',
    guaranteesTitle: 'Our',
    guaranteesTitleHighlight: 'Guarantees',
    guaranteesText: 'We stand behind every shipment with our commitment to quality, reliability, and customer satisfaction.',
    guarantees: [
      'On-time delivery or money back guarantee',
      'Professional handling of all cargo types',
      'Transparent pricing with no hidden fees',
      'Regular updates throughout transport',
      'Flexible scheduling options',
      'Customs clearance assistance'
    ],
    ctaTitle: 'Ready to Experience the Difference?',
    ctaText: 'Join thousands of satisfied clients who trust Tetralog for their transport needs.',
    getQuote: 'Get a Free Quote',
    contactUs: 'Contact Us',
    gridItems: [
      { icon: Globe, title: 'EU Wide', sub: 'Coverage' },
      { icon: Leaf, title: 'ECO', sub: 'Friendly Fleet' },
      { icon: Lock, title: 'Secure', sub: 'Transport' },
      { icon: Users, title: 'Expert', sub: 'Team' }
    ]
  };

  // Merge database content with defaults
  const content = {
    heroSubtitle: (language === 'bg' ? dbContent?.hero_subtitle_bg : dbContent?.hero_subtitle_en) || defaultContent.heroSubtitle,
    heroTitle: (language === 'bg' ? dbContent?.hero_title_bg : dbContent?.hero_title_en) || defaultContent.heroTitle,
    heroTitleHighlight: (language === 'bg' ? dbContent?.hero_title_highlight_bg : dbContent?.hero_title_highlight_en) || defaultContent.heroTitleHighlight,
    heroDescription: (language === 'bg' ? dbContent?.hero_description_bg : dbContent?.hero_description_en) || defaultContent.heroDescription,
    stats: dbContent?.stats?.map(s => ({
      number: s.number,
      label: language === 'bg' ? s.label_bg : s.label_en
    })) || defaultContent.stats,
    benefitsSubtitle: (language === 'bg' ? dbContent?.benefits_subtitle_bg : dbContent?.benefits_subtitle_en) || defaultContent.benefitsSubtitle,
    benefitsTitle: (language === 'bg' ? dbContent?.benefits_title_bg : dbContent?.benefits_title_en) || defaultContent.benefitsTitle,
    benefitsTitleHighlight: (language === 'bg' ? dbContent?.benefits_title_highlight_bg : dbContent?.benefits_title_highlight_en) || defaultContent.benefitsTitleHighlight,
    benefits: dbContent?.benefits?.map((b, i) => ({
      icon: [Shield, Truck, MapPin, Clock, Headphones, Award][i],
      title: language === 'bg' ? b.title_bg : b.title_en,
      description: language === 'bg' ? b.description_bg : b.description_en,
      highlight: language === 'bg' ? b.highlight_bg : b.highlight_en
    })) || defaultContent.benefits,
    guaranteesSubtitle: (language === 'bg' ? dbContent?.guarantees_subtitle_bg : dbContent?.guarantees_subtitle_en) || defaultContent.guaranteesSubtitle,
    guaranteesTitle: (language === 'bg' ? dbContent?.guarantees_title_bg : dbContent?.guarantees_title_en) || defaultContent.guaranteesTitle,
    guaranteesTitleHighlight: (language === 'bg' ? dbContent?.guarantees_title_highlight_bg : dbContent?.guarantees_title_highlight_en) || defaultContent.guaranteesTitleHighlight,
    guaranteesText: (language === 'bg' ? dbContent?.guarantees_text_bg : dbContent?.guarantees_text_en) || defaultContent.guaranteesText,
    guarantees: (language === 'bg' ? dbContent?.guarantees_bg : dbContent?.guarantees_en) || defaultContent.guarantees,
    ctaTitle: (language === 'bg' ? dbContent?.cta_title_bg : dbContent?.cta_title_en) || defaultContent.ctaTitle,
    ctaText: (language === 'bg' ? dbContent?.cta_text_bg : dbContent?.cta_text_en) || defaultContent.ctaText,
    getQuote: (language === 'bg' ? dbContent?.get_quote_btn_bg : dbContent?.get_quote_btn_en) || defaultContent.getQuote,
    contactUs: (language === 'bg' ? dbContent?.contact_btn_bg : dbContent?.contact_btn_en) || defaultContent.contactUs,
    gridItems: dbContent?.grid_items?.map((g, i) => ({
      icon: [Globe, Leaf, Lock, Users][i],
      title: language === 'bg' ? g.title_bg : g.title_en,
      sub: language === 'bg' ? g.sub_bg : g.sub_en
    })) || defaultContent.gridItems
  };

  return (
    <div data-testid="why-choose-us-page" className="pt-20 bg-slate-50">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <span className="overline mb-4 block">{content.heroSubtitle}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6">
            {content.heroTitle} <span className="text-[#C41E3A]">{content.heroTitleHighlight}</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl">{content.heroDescription}</p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {content.stats.map((stat, index) => (
              <div key={stat.label} className="text-center animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="text-3xl md:text-4xl font-extrabold text-[#C41E3A] mb-1">{stat.number}</div>
                <div className="text-slate-500 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section data-testid="benefits-section" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="overline mb-4 block">{content.benefitsSubtitle}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              {content.benefitsTitle} <span className="text-[#C41E3A]">{content.benefitsTitleHighlight}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.benefits.map((benefit, index) => (
              <Card key={benefit.title} data-testid={`benefit-card-${benefit.title.toLowerCase().replace(/\s+/g, '-')}`} className="bg-slate-50 border-slate-100 hover:shadow-lg transition-all duration-300 group rounded-3xl animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                      <benefit.icon className="w-6 h-6 text-[#C41E3A]" />
                    </div>
                    <span className="text-xs font-bold text-[#C41E3A] bg-red-50 px-3 py-1 rounded-full">{benefit.highlight}</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{benefit.title}</h3>
                  <p className="text-slate-500 text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantees */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="overline mb-4 block">{content.guaranteesSubtitle}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {content.guaranteesTitle} <span className="text-[#C41E3A]">{content.guaranteesTitleHighlight}</span>
              </h2>
              <p className="text-slate-500 mb-8">{content.guaranteesText}</p>
              <div className="space-y-4">
                {content.guarantees.map((guarantee, index) => (
                  <div key={index} className="flex items-center gap-3 animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                    <CheckCircle className="w-5 h-5 text-[#C41E3A] flex-shrink-0" />
                    <span className="text-slate-600">{guarantee}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {content.gridItems.map((item, i) => (
                <Card key={i} className="bg-white border-slate-100 p-6 text-center rounded-3xl">
                  <item.icon className={`w-12 h-12 ${item.title === 'ECO' ? 'text-green-500' : 'text-[#C41E3A]'} mx-auto mb-4`} />
                  <h4 className="font-bold text-slate-900 mb-1">{item.title}</h4>
                  <p className="text-slate-500 text-sm">{item.sub}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#C41E3A] to-[#A01830]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{content.ctaTitle}</h2>
          <p className="text-white/80 mb-8">{content.ctaText}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`${localizedPath('/contact')}?quote=true`}>
              <button data-testid="why-quote-btn" className="btn-modern btn-light btn-lg">
                {content.getQuote}
              </button>
            </Link>
            <Link to={localizedPath('/contact')}>
              <button data-testid="why-contact-btn" className="btn-modern btn-lg" style={{ background: 'transparent', color: 'white', border: '2px solid rgba(255,255,255,0.5)' }}>
                {content.contactUs}
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
